import { compressionCodecType, Dataset, JsonDataset, LinkedService, Metamodel, Named, Owned } from 'shell-models-lib';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DatasetTypeHandler } from 'app/components/create-datamapper/utils/datasettype-handler';
import { IMapperConnectionInfo, IMapperEntity, IMapperTable } from 'app/model/adfDataMapper/IMapperTable';
import { DataFlowDatasetSettings, DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { ensureAllPathsAreUnique, getAdlsDatasetForPreview, initializeCommonConnectionProperties, processEachADLSEntity } from 'app/components/create-datamapper/utils/adls-component-utils';
import { DslConnectorConstants } from 'app/model/adapters/mappingArtifact/DslConnectorConstants';
import { createDataflowExpression, DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { IDataMapperJsonFileOptions } from 'app/model/adfDataMapper/IDataMapperFileOptions';
import { levelOptions } from 'app/shared/components/compression-setting/compression-options';
import { delimitedTextEncodingNameOptionsForDataflow } from 'app/registries/consts/v2Dataset-const';
import { documentFormOptions } from 'app/authoring/component/transformations/complex-type-utils';
import { AdfDataMapperJsonSettings } from 'app/model/adfDataMapper/adfDataMapperJsonSettings';
import { JsonSettings } from 'app/model/dataflow/dataset-settings/json-settings';
import { getDslPropertyValue } from 'app/model/adfDataMapper/mapperTable.utils';

export class JsonDatasetTypeHandler extends DatasetTypeHandler {

    constructor(
        public isSource: Boolean
     ){
         super();
     }

    public populateSourceProperties(inputSource: DataFlowSource, outputSource: DataFlowSource, selectedDatasetFormat: DatasetFormatType) {
        outputSource.datasetSettings = Owned.create(Metamodel.modelByName('JsonSettings'), outputSource);
        const incomingSourceDatasetsSettings = inputSource.datasetSettings as AdfDataMapperJsonSettings;
        const outputSourceDatasetsSettings = outputSource.datasetSettings as JsonSettings;
        outputSource.datasetSettings.initialize('');
        outputSourceDatasetsSettings.container.expression = !incomingSourceDatasetsSettings.container ? undefined : incomingSourceDatasetsSettings.container.expression;
        outputSourceDatasetsSettings.folderPath.expression = !incomingSourceDatasetsSettings.folderPath ? undefined : incomingSourceDatasetsSettings.folderPath.expression;
        outputSourceDatasetsSettings.compressionType = !incomingSourceDatasetsSettings.compressionType ? undefined : incomingSourceDatasetsSettings.compressionType;
        outputSourceDatasetsSettings.compressionLevel.expression = !incomingSourceDatasetsSettings.compressionLevel ? undefined : incomingSourceDatasetsSettings.compressionLevel.expression;
        outputSourceDatasetsSettings.encoding.expression = !incomingSourceDatasetsSettings.encoding ? undefined : incomingSourceDatasetsSettings.encoding.expression;
        outputSource.wildcardPaths = inputSource.wildcardPaths;
        outputSource.datasetSettings = outputSourceDatasetsSettings;
        // JSON file options
        outputSource.documentForm.expression = incomingSourceDatasetsSettings.documentForm.expression;
        outputSource.unquotedColumnNames = incomingSourceDatasetsSettings.unquotedColumnNames ?? false;
        outputSource.singleQuoted = incomingSourceDatasetsSettings.singleQuoted ?? false;
        outputSource.hasComments = incomingSourceDatasetsSettings.hasComments ?? false;
        outputSource.backslashEscape = incomingSourceDatasetsSettings.backslashEscape ?? false;
    }

    public getOkButtonStatus(selectedRows: IMapperEntity[]): [boolean, string] {
        let disableButton = true;
        let disableButtonStatus = '';
        if (selectedRows.length) {
            disableButton = false;
            const rows = selectedRows as IMapperEntity[];
            rows.forEach((elem: IMapperEntity) => {
                const containerPath = (elem.dataset as JsonDataset)?.location?.getLocation()?.container;
                if (!containerPath) {
                    disableButton = true;
                }
            });
            if (!disableButton) {
                [disableButton, disableButtonStatus] = !ensureAllPathsAreUnique(rows) ? [true, this.isSource ? stringResources.DuplicateSourceEntitySelected : stringResources.DuplicateTargetEntitySelected] : [false, ''];
            }
            return [disableButton, disableButtonStatus];
        }
        return [disableButton, disableButtonStatus];
    }

    public processSelectedRows(
        selectedRows: IMapperEntity[],
        selectedSourceLinkedService: LinkedService,
        selectedDatasetFormat: DatasetFormatType,
        selectedSourceFileOptions: DataFlowDatasetSettings): IMapperTable[] {

        const tables: IMapperTable[] = [];
        selectedRows.forEach((elem) => {
            const filePath = (elem.dataset as JsonDataset).location.getLocation();
            processEachADLSEntity(filePath, selectedSourceLinkedService, elem, selectedDatasetFormat, tables, this.isSource);
        });

        tables.forEach((elem) => {
            const jsonDataSource = elem as DataFlowSource;
            const selectedJsonFileSettings = selectedSourceFileOptions as AdfDataMapperJsonSettings;
            if (!!selectedJsonFileSettings) {
                jsonDataSource.documentForm = selectedJsonFileSettings.documentForm;
                jsonDataSource.unquotedColumnNames = selectedJsonFileSettings.unquotedColumnNames;
                jsonDataSource.singleQuoted = selectedJsonFileSettings.singleQuoted;
                jsonDataSource.hasComments = selectedJsonFileSettings.hasComments;
                jsonDataSource.backslashEscape = selectedJsonFileSettings.backslashEscape;
            }
        });
        return tables;
    }

    public getFileOptions(): IDataMapperJsonFileOptions {
        return {
            compressionTypeOptions: [
                {
                    label: stringResources.None,
                    value: undefined
                },
                {
                    label: compressionCodecType.bzip2,
                    value: compressionCodecType.bzip2
                },
                {
                    label: compressionCodecType.gzip,
                    value: compressionCodecType.gzip
                },
                {
                    label: compressionCodecType.deflate,
                    value: compressionCodecType.deflate
                },
                {
                    label: compressionCodecType.ZipDeflate,
                    value: compressionCodecType.ZipDeflate
                },
                {
                    label: compressionCodecType.snappy,
                    value: compressionCodecType.snappy
                },
                {
                    label: compressionCodecType.lz4,
                    value: compressionCodecType.lz4
                },
                {
                    label: compressionCodecType.TarGZip,
                    value: compressionCodecType.TarGZip
                },
                {
                    label: compressionCodecType.tar,
                    value: compressionCodecType.tar
                }
            ],
            compressionLevelOptions: levelOptions,
            encodingOptions: delimitedTextEncodingNameOptionsForDataflow,
            documentFormOptions
        };
    }

    public getDefaultFileSettings(): DataFlowDatasetSettings {
        const dataFlowSource = DataFlowSource.MODEL.create<Named>();
        const jsonSettings: AdfDataMapperJsonSettings = Owned.create<AdfDataMapperJsonSettings>(AdfDataMapperJsonSettings.MODEL, dataFlowSource);
        jsonSettings.initialize(undefined);
        return jsonSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetSettings(dslConnectorProperties: Map<string, any>, table: IMapperTable): DataFlowDatasetSettings {
        // Read dsl properties
        const container = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.container);
        const folderPath = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.folderPath);
        const compressionType = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.compressionCodec);
        const compressionLevel = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.compressionLevel);
        const encoding = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.encoding);

        const documentForm = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.documentForm);
        const backslashEscape = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.backslashEscape);
        const hasComments = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.hasComments);
        const unquotedColumnNames = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.unquotedColumnNames);
        const singleQuoted = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.singleQuoted);

        // Create and populate JsonSettings
        const jsonSettings = Owned.create<AdfDataMapperJsonSettings>(AdfDataMapperJsonSettings.MODEL, table as DataFlowSource);
        jsonSettings.initialize(undefined);

        jsonSettings.container = createDataflowExpression(container) as DataFlowExpression;
        jsonSettings.folderPath = createDataflowExpression(folderPath) as DataFlowExpression;
        jsonSettings.compressionType = compressionType ?? undefined;
        jsonSettings.unquotedColumnNames = unquotedColumnNames;
        jsonSettings.hasComments = hasComments;
        jsonSettings.singleQuoted = singleQuoted;
        jsonSettings.backslashEscape = backslashEscape;
        if (!!documentForm) {
            jsonSettings.documentForm = createDataflowExpression(documentForm) as DataFlowExpression;
        }
        if(!!compressionLevel) {
            jsonSettings.compressionLevel = createDataflowExpression(compressionLevel) as DataFlowExpression;
        }
        if(!!encoding) {
            jsonSettings.encoding = createDataflowExpression(encoding) as DataFlowExpression;
        }

        return jsonSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetForPreview(dslConnectorProperties: Map<string, any>, linkedService: LinkedService): Dataset {
        const container = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.container);
        const folderPath = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.folderPath);
        return getAdlsDatasetForPreview(DatasetFormatType.Json, container, folderPath, linkedService);
    }

    public setCommonDslConnectionProperties(mapperConnectionInfo: IMapperConnectionInfo, selectedDatasetFormat: DatasetFormatType, selectedSourceFileOptions: DataFlowDatasetSettings): void {
        this.setCommonDslDriftProperties(mapperConnectionInfo);
        initializeCommonConnectionProperties(mapperConnectionInfo.connection, selectedDatasetFormat, this.isSource);
        // Add json dataset settings
        if (this.isSource) {
            const jsonSettings = selectedSourceFileOptions as AdfDataMapperJsonSettings;
            if (!!jsonSettings.compressionType) {
                mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.compressionCodec, jsonSettings.compressionType);
                mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.compressionLevel, jsonSettings.compressionLevel.expression);
            }
            if (!!jsonSettings.encoding.expression) {
                mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.encodingName, jsonSettings.encoding.expression);
            }
            if (!!jsonSettings.documentForm.expression) {
                mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.documentForm, jsonSettings.documentForm.expression);
            }
            if (jsonSettings.unquotedColumnNames) {
                mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.unquotedColumnNames, jsonSettings.unquotedColumnNames);
            }
            if (jsonSettings.hasComments) {
                mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.hasComments, jsonSettings.hasComments);
            }
            if (jsonSettings.singleQuoted) {
                mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.singleQuoted, jsonSettings.singleQuoted);
            }
            if (jsonSettings.backslashEscape) {
                mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.backslashEscape, jsonSettings.backslashEscape);
            }
        }
    }
}
